/**
 * useAPICall Hook
 * Standard pattern for API requests with loading/error/data states
 * 
 * Features:
 * - Automatic loading state management
 * - Error handling with user-friendly messages
 * - Toast notifications
 * - Retry logic
 * - Request cancellation on component unmount
 * 
 * @example
 * const { loading, error, data, execute } = useAPICall();
 * 
 * const handleSearch = async () => {
 *   try {
 *     await execute(
 *       API_CONFIG.hcp.search,
 *       { name: 'John' },
 *       {
 *         successMessage: 'Search completed',
 *         errorMessage: 'Search failed',
 *         retries: 1
 *       }
 *     );
 *   } catch (err) {
 *     // Error already handled
 *   }
 * };
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient, { CancelToken, isCancel, retryRequest } from '../services/apiClient';
import { buildEndpoint } from '../config/api.config';
import { toast } from 'react-toastify';

const useAPICall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const cancelTokenRef = useRef(null);

  // Cleanup on unmount - cancel pending requests
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  /**
   * Execute API Call
   * 
   * @param {object} apiConfig - API configuration { endpoint, method }
   * @param {object} payload - Request payload (body/params)
   * @param {object} options - Additional options
   * @param {string} options.successMessage - Toast message on success
   * @param {string} options.errorMessage - Toast message on error
   * @param {number} options.retries - Number of retry attempts (default: 0)
   * @param {boolean} options.showSuccessToast - Show success toast (default: false)
   * @param {boolean} options.showErrorToast - Show error toast (default: true)
   * @param {object} options.params - URL parameters for dynamic endpoints
   * @param {number} options.timeoutMs - Request timeout override in milliseconds
   * @returns {Promise} - Resolved data or rejected error
   */
  const execute = useCallback(async (apiConfig, payload = null, options = {}) => {
    const {
      successMessage,
      errorMessage = 'Operation failed. Please try again.',
      retries = 0,
      showSuccessToast = false,
      showErrorToast = true,
      params = {},
      headers = {},
      timeoutMs,
    } = options;

    // Reset state
    setError(null);
    setLoading(true);

    // Create cancel token
    const tokenSource = CancelToken.source();
    cancelTokenRef.current = tokenSource;

    // Build endpoint with dynamic parameters
    const endpoint = params && Object.keys(params).length > 0
      ? buildEndpoint(apiConfig.endpoint, params)
      : apiConfig.endpoint;

    // Create request function
    const makeRequest = async () => {
      const config = {
        url: endpoint,
        method: apiConfig.method,
        cancelToken: tokenSource.token,
      };

      if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
        config.timeout = timeoutMs;
      }

      if (headers && Object.keys(headers).length > 0) {
        config.headers = headers;
      }

      // Add payload based on method
      if (['POST', 'PUT', 'PATCH'].includes(apiConfig.method.toUpperCase())) {
        config.data = payload;
      } else if (payload) {
        config.params = payload;
      }

      return await apiClient(config);
    };

    try {
      // Execute request with retry logic
      const response = retries > 0
        ? await retryRequest(makeRequest, retries)
        : await makeRequest();

      // Set data
      setData(response.data);

      // Show success toast
      if (showSuccessToast && successMessage) {
        toast.success(successMessage);
      }

      return response.data;

    } catch (err) {
      // Don't handle cancelled requests
      if (isCancel(err)) {
        return;
      }

      const isTimeoutError =
        err?.code === 'ECONNABORTED'
        || String(err?.message || '').toLowerCase().includes('timeout');

      // Extract user-friendly error message
      const userMessage = isTimeoutError
        ? (errorMessage || 'Request timed out. Please try again.')
        : (
          err.response?.data?.message
          || err.response?.data?.error
          || err.response?.data?.status
          || errorMessage
        );

      // Set error state
      setError(userMessage);

      const isUnauthorizedError = err.response?.status === 401;

      // Show error toast
      if (showErrorToast && !isUnauthorizedError) {
        toast.error(userMessage);
      }

      // Re-throw error for component-level handling
      throw err;

    } finally {
      setLoading(false);
      cancelTokenRef.current = null;
    }
  }, []);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    loading,
    error,
    data,
    execute,
    reset
  };
};

export default useAPICall;
